import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import FirstAccessPage from "@/pages/FirstAccessPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import Dashboard from "@/pages/Dashboard";
import BulletinPage from "@/pages/BulletinPage";
import ProceduresPage from "@/pages/ProceduresPage";
import DocumentsPage from "@/pages/DocumentsPage";
import TrainingsPage from "@/pages/TrainingsPage";
import CampaignsPage from "@/pages/CampaignsPage";
import UrgentNewsPage from "@/pages/UrgentNewsPage";
import FormsPage from "@/pages/FormsPage";
import InternalInfoPage from "@/pages/InternalInfoPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import GalleryPage from "@/pages/GalleryPage";
import OrgChartPage from "@/pages/OrgChartPage";
import BirthdaysPage from "@/pages/BirthdaysPage";
import UsefulLinksPage from "@/pages/UsefulLinksPage";
import ContactsPage from "@/pages/ContactsPage";
import AdminPage from "@/pages/AdminPage";
import AdminTotalPage from "@/pages/AdminTotalPage";
import ContentManagementPage from "@/pages/ContentManagementPage";
import ManagerContentPage from "@/pages/ManagerContentPage";
import ProfilePage from "@/pages/ProfilePage";

const ADMIN_ROLES = ["administrador_total", "admin"];
const AUTHOR_ROLES = ["autor_area", "administrador_total", "admin"];
const MANAGER_ROLES = ["gerente", "administrador_total", "admin"];
const FORM_ROLES = ["gerente", "autor_area", "administrador_total", "admin", "area_manager", "coordinator", "regional_manager", "store_manager"];

function ProtectedRoute({ children, adminOnly = false, allowedRoles = null }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#2346ff] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm font-medium">Carregando...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/primeiro-acesso" element={<FirstAccessPage />} />
      <Route path="/trocar-senha" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/boletim" element={<ProtectedRoute><Layout><BulletinPage /></Layout></ProtectedRoute>} />
      <Route path="/procedimentos" element={<ProtectedRoute><Layout><ProceduresPage /></Layout></ProtectedRoute>} />
      <Route path="/documentos" element={<ProtectedRoute><Layout><DocumentsPage /></Layout></ProtectedRoute>} />
      <Route path="/treinamentos" element={<ProtectedRoute><Layout><TrainingsPage /></Layout></ProtectedRoute>} />
      <Route path="/campanhas" element={<ProtectedRoute><Layout><CampaignsPage /></Layout></ProtectedRoute>} />
      <Route path="/ultima-hora" element={<ProtectedRoute><Layout><UrgentNewsPage /></Layout></ProtectedRoute>} />
      <Route path="/formularios" element={<ProtectedRoute allowedRoles={FORM_ROLES}><Layout><FormsPage /></Layout></ProtectedRoute>} />
      <Route path="/informacoes" element={<ProtectedRoute><Layout><InternalInfoPage /></Layout></ProtectedRoute>} />
      <Route path="/mural" element={<ProtectedRoute><Layout><AnnouncementsPage /></Layout></ProtectedRoute>} />
      <Route path="/galeria" element={<ProtectedRoute><Layout><GalleryPage /></Layout></ProtectedRoute>} />
      <Route path="/organograma" element={<ProtectedRoute><Layout><OrgChartPage /></Layout></ProtectedRoute>} />
      <Route path="/aniversariantes" element={<ProtectedRoute><Layout><BirthdaysPage /></Layout></ProtectedRoute>} />
      <Route path="/links-uteis" element={<ProtectedRoute><Layout><UsefulLinksPage /></Layout></ProtectedRoute>} />
      <Route path="/contatos" element={<ProtectedRoute><Layout><ContactsPage /></Layout></ProtectedRoute>} />
      <Route path="/gestao-conteudo" element={<ProtectedRoute allowedRoles={AUTHOR_ROLES}><Layout><ContentManagementPage /></Layout></ProtectedRoute>} />
      <Route path="/conteudos-gerencia" element={<ProtectedRoute allowedRoles={MANAGER_ROLES}><Layout><ManagerContentPage /></Layout></ProtectedRoute>} />
      <Route path="/admin-total" element={<ProtectedRoute allowedRoles={ADMIN_ROLES}><Layout><AdminTotalPage /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Layout><AdminPage /></Layout></ProtectedRoute>} />
      <Route path="/perfil" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
